import { Reducer } from "redux";

import { Product, ProductCount } from "@/Contracts/Products";

import { ProductsActionTypes, ProductsActions } from "../../Actions/Products";
import {
	CustomViewResponse,
	CustomView,
	CustomViewsMetadata
} from "@/Contracts/UI";

export interface ProductsState {
	readonly isLoading: boolean;
	readonly productList: Product[];
	readonly productCount: ProductCount;
	readonly currentActiveProduct: Product | null;
	readonly addProductDrawerOpen: boolean;
	readonly editProductDrawerOpen: boolean;
	readonly addEtilizeProductDrawerOpen: boolean;
	readonly selectedEtilizeProduct: Product | null;
	readonly viewList: CustomViewResponse | null;
	readonly currentActiveView: CustomView | null;
	readonly viewMetadata: CustomViewsMetadata | null;
	readonly isViewChanged: boolean;
	readonly isBulkActionsRecordsSelected: boolean;
}

export const initialState: ProductsState = {
	isLoading: false,
	productList: new Array<Product>(),
	productCount: new ProductCount(),
	currentActiveProduct: null,
	addProductDrawerOpen: false,
	editProductDrawerOpen: false,
	addEtilizeProductDrawerOpen: false,
	selectedEtilizeProduct: null,
	viewList: null,
	currentActiveView: null,
	viewMetadata: null,
	isViewChanged: false,
	isBulkActionsRecordsSelected: false
};

export const ProductsReducer: Reducer<ProductsState, ProductsActions> = (
	state: ProductsState = initialState,
	action: ProductsActions
): ProductsState => {
	switch (action.type) {
		case ProductsActionTypes.SHOW_PRODUCTS_LOADER: {
			return {
				...state,
				isLoading: true
			};
		}
		case ProductsActionTypes.HIDE_PRODUCTS_LOADER: {
			return {
				...state,
				isLoading: false
			};
		}
		case ProductsActionTypes.PRODUCTS_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.PRODUCTS_SUCCESSFUL: {
			return {
				...state,
				productList: action.productList,
				productCount: action.productCount
			};
		}
		case ProductsActionTypes.PRODUCTS_FAILED: {
			return state;
		}
		case ProductsActionTypes.PRODUCTS_RESET: {
			return {
				...state,
				productList: new Array<Product>(),
				productCount: new ProductCount()
			};
		}
		case ProductsActionTypes.PRODUCT_DETAILS_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.PRODUCT_DETAILS_SUCCESSFUL: {
			return {
				...state,
				currentActiveProduct: action.currentActiveProduct
			};
		}
		case ProductsActionTypes.PRODUCT_DETAILS_FAILED: {
			return state;
		}
		case ProductsActionTypes.PRODUCT_DETAILS_UPDATED: {
			return {
				...state,
				currentActiveProduct: action.currentActiveProduct
			};
		}
		case ProductsActionTypes.PRODUCT_DETAILS_RESET: {
			return {
				...state,
				currentActiveProduct: null
			};
		}
		case ProductsActionTypes.DELETE_PRODUCT_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.DELETE_PRODUCT_SUCCESSFUL: {
			return {
				...state,
				productList: state.productList.filter(
					product => product.id !== action.deleteProduct.id
				)
			};
		}
		case ProductsActionTypes.DELETE_PRODUCT_FAILED: {
			return state;
		}
		case ProductsActionTypes.ADD_PRODUCT_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.ADD_PRODUCT_SUCCESSFUL: {
			return {
				...state,
				productList: [action.addProduct].concat(state.productList)
			};
		}
		case ProductsActionTypes.ADD_PRODUCT_FAILED: {
			return state;
		}
		case ProductsActionTypes.EDIT_PRODUCT_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.EDIT_PRODUCT_SUCCESSFUL: {
			const productList = state.productList.filter(
				product => product.id !== action.editProduct.id
			);
			const updateProductIndex = state.productList.findIndex(
				product => product.id === action.editProduct.id
			);
			if (updateProductIndex > -1) {
				productList.splice(updateProductIndex, 0, action.editProduct);
			}
			return {
				...state,
				productList
			};
		}
		case ProductsActionTypes.EDIT_PRODUCT_FAILED: {
			return state;
		}
		case ProductsActionTypes.ADD_PRODUCT_DRAWER_OPENED: {
			return {
				...state,
				addProductDrawerOpen: true
			};
		}
		case ProductsActionTypes.ADD_PRODUCT_DRAWER_CLOSED: {
			return {
				...state,
				addProductDrawerOpen: false
			};
		}
		case ProductsActionTypes.EDIT_PRODUCT_DRAWER_OPENED: {
			return {
				...state,
				editProductDrawerOpen: true
			};
		}
		case ProductsActionTypes.EDIT_PRODUCT_DRAWER_CLOSED: {
			return {
				...state,
				editProductDrawerOpen: false
			};
		}
		case ProductsActionTypes.ADD_ETILIZE_PRODUCT_DRAWER_OPENED: {
			return {
				...state,
				addEtilizeProductDrawerOpen: true
			};
		}
		case ProductsActionTypes.ADD_ETILIZE_PRODUCT_DRAWER_CLOSED: {
			return {
				...state,
				addEtilizeProductDrawerOpen: false
			};
		}
		case ProductsActionTypes.SET_SELECTED_ETILIZE_PRODUCT: {
			return {
				...state,
				selectedEtilizeProduct: action.selectedProduct
			};
		}
		case ProductsActionTypes.RESET_SELECTED_ETILIZE_PRODUCT: {
			return {
				...state,
				selectedEtilizeProduct: null
			};
		}
		case ProductsActionTypes.CLONE_PRODUCT_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.CLONE_PRODUCT_SUCCESSFUL: {
			return {
				...state,
				productList: [action.cloneProduct].concat(state.productList)
			};
		}
		case ProductsActionTypes.CLONE_PRODUCT_FAILED: {
			return state;
		}
		case ProductsActionTypes.FETCH_PRODUCT_VIEW_METADATA_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.FETCH_PRODUCT_VIEW_METADATA_SUCCESSFUL: {
			return {
				...state,
				viewMetadata: action.metadata
			};
		}
		case ProductsActionTypes.FETCH_PRODUCT_VIEW_METADATA_FAILED: {
			return state;
		}
		case ProductsActionTypes.FETCH_ALL_PRODUCT_VIEWS_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.FETCH_ALL_PRODUCT_VIEWS_SUCCESSFUL: {
			return {
				...state,
				viewList: action.viewList
			};
		}
		case ProductsActionTypes.FETCH_ALL_PRODUCT_VIEWS_FAILED: {
			return state;
		}
		case ProductsActionTypes.FETCH_PRODUCT_LIST_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.FETCH_PRODUCT_LIST_SUCCESSFUL: {
			return {
				...state,
				productList: action.productList.data,
				productCount: action.productList.filtered_count_map
			};
		}
		case ProductsActionTypes.FETCH_PRODUCT_LIST_FAILED: {
			return state;
		}
		case ProductsActionTypes.CREATE_PRODUCT_VIEW_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.CREATE_PRODUCT_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: [...(state.viewList?.data ?? []), action.view]
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case ProductsActionTypes.CREATE_PRODUCT_VIEW_FAILED: {
			return state;
		}
		case ProductsActionTypes.UPDATE_PRODUCT_VIEW_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.UPDATE_PRODUCT_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.map(view =>
								view.id === action.view.id ? action.view : view
							)
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case ProductsActionTypes.UPDATE_PRODUCT_VIEW_FAILED: {
			return state;
		}
		case ProductsActionTypes.DELETE_PRODUCT_VIEW_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.DELETE_PRODUCT_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.initialView,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.filter(
								view => view.id !== action.deletedView.id
							)
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case ProductsActionTypes.DELETE_PRODUCT_VIEW_FAILED: {
			return state;
		}
		case ProductsActionTypes.UPDATE_PRODUCTS_CURRENT_ACTIVE_VIEW: {
			return {
				...state,
				currentActiveView: action.currentActiveView,
				isViewChanged: action.isViewChanged
			};
		}
		case ProductsActionTypes.RESET_PRODUCTS_CURRENT_ACTIVE_VIEW: {
			return {
				...state,
				currentActiveView: null,
				isViewChanged: false,
				viewList: null,
				viewMetadata: null
			};
		}
		case ProductsActionTypes.BULK_ACTION_RECORDS_SELECTED: {
			return {
				...state,
				isBulkActionsRecordsSelected: true
			};
		}
		case ProductsActionTypes.BULK_ACTION_RECORDS_RESET: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case ProductsActionTypes.BULK_DELETE_RECORDS_REQUESTED: {
			return {
				...state
			};
		}
		case ProductsActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case ProductsActionTypes.BULK_DELETE_RECORDS_FAILED: {
			return {
				...state
			};
		}
		case ProductsActionTypes.BULK_UPDATE_RECORDS_REQUESTED: {
			return {
				...state
			};
		}
		case ProductsActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case ProductsActionTypes.BULK_UPDATE_RECORDS_FAILED: {
			return {
				...state
			};
		}
		case ProductsActionTypes.MERGE_DUPLICATE_PRODUCTS_REQUESTED: {
			return state;
		}
		case ProductsActionTypes.MERGE_DUPLICATE_PRODUCTS_SUCCESSFUL: {
			return {
				...state,
				currentActiveProduct: action.currentActiveProduct
			};
		}
		case ProductsActionTypes.MERGE_DUPLICATE_PRODUCTS_FAILED: {
			return state;
		}
		default: {
			return state;
		}
	}
};

export default ProductsReducer;
